import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Counter } from '@adornis/counter/counter.js';
import { Payment } from '@adornis/payments/db/payments.js';
import { ExamType, type OrderStatus } from '@ils/static/enums.js';
import { Names } from '@ils/static/names.js';
import { DateTime } from 'luxon';
import type { InsertOneResult, UpdateResult } from 'mongodb';
import {
  createInvoiceResolver,
  createNewOrderManuallyResolver,
  getOrCalculateRefundForOrderID,
  getOrderByExamDayAndUserResolver,
  getOrdersByExamDayAndTraineesResolver,
  isExamDayPassedResolver,
  isExamDayRatedResolver,
  isOrderRefundableResolver,
  manuallySetOrderPaidResolver,
  manuallySetOrderRevokedResolver,
  moveOrderManuallyResolver,
  orderAfterCreate,
  orderAfterUpdate,
  orderBeforeCreate,
  orderBeforeUpdate,
  recreateInvoiceResolver,
  subscribeOrdersByUserIDResolver,
} from '../server/resolver/order.js';
import { ILSProduct } from './ils-product.js';

const ExamTypeShorts: Record<ExamType, string> = {
  [ExamType.FAMED]: 'FM',
  [ExamType.FSPP]: 'FSPP',
};

export async function getIncrementingInvoiceNumber(date: DateTime, examType: ExamType) {
  const invoiceCounter = await Counter.getIncrementingNumber(
    examType === ExamType.FAMED ? 'invoice' : `invoice-${examType}`,
  )();
  return `IV-${date.year}-${ExamTypeShorts[examType]}-${invoiceCounter.toString().padStart(6, '0')}`;
}

export async function getIncrementingOrderNumber(date: DateTime, examType: ExamType) {
  const orderCounter = await Counter.getIncrementingNumber(
    examType === ExamType.FAMED ? 'order' : `order-${examType}`,
  )();
  return `OD-${date.year}-${ExamTypeShorts[examType]}-${orderCounter.toString().padStart(6, '0')}`;
}

/**
 * @entity Order
 */
@Entity()
export class Order extends Payment {
  static override _class = Names.Order._class;
  static override _collectionName = Names.Order._collectionName;

  // @ts-expect-error expand type
  override status!: OrderStatus;

  static override get allFields() {
    return {
      ...selectionSet(() => this, 2, ['statusUpdates', 'isExamDayRated', 'isExamDayPassed']),
      products: {
        amount: 1,
        product: selectionSet(() => ILSProduct, 4, ['candidates']),
      },
    } as const;
  }

  @Field(type => Boolean)
  public confirmed?: boolean;

  @Field(type => Date)
  public revokedAt: Maybe<Date>;

  @Field(type => String, { default: v => v ?? null })
  public invoiceNumber?: string;

  @Field(type => String, { default: v => v ?? '' })
  public orderNumber!: string;

  @Field(type => String)
  public comment?: string;

  @Field(type => ID)
  public certificate: Maybe<string>;

  @Field(type => ID)
  movedFromOrder: Maybe<string>;

  // ist der nutzer auf dem verlinkten examday fertig bewertet
  /** @deprecated use scores */
  @Field(type => Boolean, {
    resolve: function (this: Order) {
      return isExamDayRatedResolver(this);
    },
  })
  isExamDayRated: Maybe<boolean>;

  // hat der nutzer auf dem verlinkten examday bestanden
  /** @deprecated use scores */
  @Field(type => Boolean, {
    resolve: function (this: Order) {
      return isExamDayPassedResolver(this);
    },
  })
  isExamDayPassed: Maybe<boolean>;

  constructor(doc: PartialEntityData<Order>) {
    // @ts-expect-error override of status type
    super(doc);
  }

  override create(): Promise<string> {
    if (!A.isServer)
      throw new Error("You are only allowed to create Orders on the server! Use 'createNewOrderManually' instead.");

    return super.create();
  }

  static override async beforeCreate(order: Order): Promise<void> {
    await super.beforeCreate(order);
    await orderBeforeCreate(order);
  }

  static override async afterCreate(order: Order, result: InsertOneResult): Promise<void> {
    await super.afterCreate(order, result);
    await orderAfterCreate(order, result);
  }

  static override async beforeUpdate(oldOrder: EntityData<Order>, update: Order) {
    await orderBeforeUpdate(oldOrder, update);
    return super.beforeUpdate(oldOrder, update);
  }

  static override async afterUpdate(oldOrder: EntityData<Order>, newEntity: Order, result: UpdateResult) {
    await super.afterUpdate(oldOrder, newEntity, result);
    await orderAfterUpdate(oldOrder, newEntity, result);
  }
}

// @((?:Mutation)|(?:Query)|(?:Subscription))\(type => ([^)]+)\)\nstatic ([^(]+)(?:\(\n((?:  @Arg\('[^']+', type => [^)]+\)[^,]+,\n)+)\)|\(\)) \{\n  return ([^(]+)\(\)\(\);\n\}
// export const $3 = register$1({operationName: '$3', type: () => $2, resolver: $5, params: [\n$4\n]})

// @Arg\('([^']+)', type => ([^)]+)\)[^,]+,
// { name: '$1', type: () => $2 },

export const moveOrderManually = registerMutation({
  operationName: 'moveOrderManually',
  type: () => Order,
  resolve: moveOrderManuallyResolver,
  params: [
    { name: 'oldOrderID', type: () => String },
    { name: 'oldExamDayID', type: () => String },
    { name: 'newExamDayID', type: () => String },
    { name: 'bookingDate', type: () => DateTime },
    { name: 'userID', type: () => String },
  ],
});

export const createNewOrderManually = registerMutation({
  operationName: 'createNewOrderManually',
  type: () => Order,
  resolve: createNewOrderManuallyResolver,
  params: [
    { name: 'examDayID', type: () => String },
    { name: 'date', type: () => DateTime },
    { name: 'userID', type: () => String },
  ],
});

export const createInvoice = registerMutation({
  operationName: 'createInvoice',
  type: () => String,
  resolve: createInvoiceResolver,
  params: [{ name: 'orderID', type: () => String }],
});

export const recreateInvoice = registerMutation({
  operationName: 'recreateInvoice',
  type: () => String,
  resolve: recreateInvoiceResolver,
  params: [{ name: 'orderID', type: () => String }],
});

export const getOrdersByExamDayAndTrainees = registerQuery({
  operationName: 'getOrdersByExamDayAndTrainees',
  type: () => [Order],
  resolve: getOrdersByExamDayAndTraineesResolver,
  params: [
    { name: 'examDayID', type: () => String },
    { name: 'traineeIDs', type: () => [String] },
  ],
});

export const getOrderByExamDayAndUser = registerQuery({
  operationName: 'getOrderByExamDayAndUser',
  type: () => Order,
  resolve: getOrderByExamDayAndUserResolver,
  params: [
    { name: 'examDayID', type: () => String },
    { name: 'userID', type: () => String },
  ],
});

export const subscribeOrdersByUserID = registerSubscription({
  operationName: 'subscribeOrdersByUserID',
  type: () => [Order],
  resolve: subscribeOrdersByUserIDResolver,
  params: [{ name: 'userID', type: () => ID }],
});

// should be deprecated in favour of manuallySetState
export const manuallySetOrderPaid = registerMutation({
  operationName: 'manuallySetOrderPaid',
  type: () => Boolean,
  resolve: manuallySetOrderPaidResolver,
  params: [{ name: 'orderID', type: () => ID }],
});

export const manuallySetOrderRevoked = registerMutation({
  operationName: 'manuallySetOrderRevoked',
  type: () => Boolean,
  resolve: manuallySetOrderRevokedResolver,
  params: [{ name: 'orderID', type: () => ID }],
});

export const getRefundAmountByOrderID = registerQuery({
  operationName: 'getRefundAmountByOrderID',
  type: () => Int,
  params: [{ name: 'orderID', type: () => ID }],
  resolve: (orderID: string) => () => getOrCalculateRefundForOrderID(orderID),
});

export const getIsRefundableForOrderID = registerQuery({
  operationName: 'getIsRefundableForOrderID',
  type: () => Boolean,
  params: [{ name: 'orderID', type: () => ID }],
  resolve: isOrderRefundableResolver,
});
